<div class="px-3 pt-3 pb-4" [attr.data-testid]="'client-profile-details-container'" [ngClass]="{'pointer-events-none opacity-60': client() && client().is_deleted}">

    <!-- Balance Section - v7 style with rounded cards and icons -->
    <div class="grid grid-nogutter gap-2 mb-3" [attr.data-testid]="'balance-section'">
        <!-- Outstanding Balance Card -->
        <div class="col p-2 border-round-lg flex flex-column relative overflow-hidden transition-colors transition-duration-200 hover:bg-pink-100" 
             style="background-color: rgba(239, 68, 68, 0.05);" 
             [attr.data-testid]="'outstanding-balance'">
            <div class="flex align-items-center gap-2 mb-1">
                <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center" style="background-color: rgba(239, 68, 68, 0.1);">
                    <i class="fa fa-chart-line text-pink-500 text-xs"></i>
                </div>
            </div>
            <span class="text-xs font-medium text-500 mb-0 uppercase letter-spacing-1" [attr.aria-label]="'outstanding-balance-label'">{{'CLIENT_PROFILE.OWES' | translate}}</span>
            @if (!showSkeleton()) {
              <strong class="text-lg text-pink-600 font-medium" style="font-variant-numeric: tabular-nums;" [attr.aria-label]="'outstanding-balance-amount'">{{(client()?.total_outstanding_in_minor_currency || 0) | currencyformat}}</strong>
            }
            @if (showSkeleton()) {
              <p-skeleton height="1.5rem" styleClass="w-full" [attr.data-testid]="'outstanding-balance-skeleton'"></p-skeleton>
            }
        </div>

        <!-- Credit Balance Card -->
        <div class="col p-2 border-round-lg flex flex-column relative overflow-hidden transition-colors transition-duration-200"
             [ngClass]="{'cursor-pointer hover:bg-green-100': (client()?.credit_balance || 0) > 0}"
             style="background-color: rgba(34, 197, 94, 0.05);"
             (click)="(client()?.credit_balance || 0) > 0 ? showCreditBalancePopup() : ''"
             [attr.data-testid]="'credit-balance'">
            <div class="flex align-items-center gap-2 mb-1">
                <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center" style="background-color: rgba(34, 197, 94, 0.1);">
                    <i class="fa fa-wallet text-green-500 text-xs"></i>
                </div>
                @if ((client()?.credit_balance || 0) > 0) {
                  <i class="fa fa-arrow-up-right text-xs text-400 opacity-0 hover-parent-opacity-100" [attr.data-testid]="'credit-balance-icon'"></i>
                }
            </div>
            <span class="text-xs font-medium text-500 mb-0 uppercase letter-spacing-1" [attr.aria-label]="'credit-balance-label'">{{'CLIENT_PROFILE.CREDIT_BALANCE' | translate}}</span>
            @if (!showSkeleton()) {
              <strong class="text-lg text-green-600 font-medium" style="font-variant-numeric: tabular-nums;" [attr.aria-label]="'credit-balance-amount'">{{(client()?.credit_balance || 0) | currencyformat}}</strong>
            }
            @if (showSkeleton()) {
              <p-skeleton height="1.5rem" styleClass="w-full" [attr.data-testid]="'credit-balance-skeleton'"></p-skeleton>
            }
        </div>

        <!-- Lifetime Value Card -->
        <div class="col p-2 border-round-lg flex flex-column relative overflow-hidden transition-colors transition-duration-200 hover:bg-blue-100" 
             style="background-color: rgba(59, 130, 246, 0.05);"
             [attr.data-testid]="'lifetime-value'">
            <div class="flex align-items-center gap-2 mb-1">
                <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center" style="background-color: rgba(59, 130, 246, 0.1);">
                    <i class="fa fa-crown text-xs" 
                       [ngClass]="{
                           'text-yellow-500': crownType(client()?.client_since || '').coin === 'gold',
                           'text-400': crownType(client()?.client_since || '').coin === 'silver',
                           'text-orange-600': crownType(client()?.client_since || '').coin === 'bronze',
                           'text-blue-500': crownType(client()?.client_since || '').coin === 'default'
                       }"></i>
                </div>
                @if (crownType(client()?.client_since || '').coin !== 'default' && !isProspect(client()?.client_status) && !isDefaultDate(client()?.client_since || '')) {
                  <img
                      width="14"
                      [src]="'/assets/img/icons/'+crownType(client()?.client_since || '').coin+'.svg'"
                      alt="Tier Icon"
                      [attr.data-testid]="'lifetime-value-icon'"
                  />
                }
            </div>
            <span class="text-xs font-medium text-500 mb-0 uppercase letter-spacing-1" [attr.aria-label]="'lifetime-value-label'">{{'CLIENT_PROFILE.LIFETIME_VALUE' | translate}}</span>
            @if (!showSkeleton()) {
              <strong class="text-lg text-blue-600 font-medium" style="font-variant-numeric: tabular-nums;" [attr.aria-label]="'lifetime-value-amount'">{{(client()?.lifetime_value || 0) | currencyformat}}</strong>
            }
            @if (showSkeleton()) {
              <p-skeleton height="1.5rem" styleClass="w-full" [attr.data-testid]="'lifetime-value-skeleton'"></p-skeleton>
            }
        </div>
    </div>

    <form [formGroup]="updateClientFormGroup" [attr.data-testid]="'client-profile-form'">
        
        <!-- Client Info Card - v7 style -->
        <div class="text-sm text-600 pb-2" [attr.data-testid]="'client-info-card'">
            
            <!-- Home Club Row -->
            <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'home-club-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-building text-xs text-500"></i>
                    </div>
                    @if (!showSkeleton() && !isProspect(client()?.client_status)) {
                      <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'home-club-label'">{{'CLIENT_PROFILE.HOME_CLUB' | translate}}</span>
                    }
                    @if (!showSkeleton() && isProspect(client()?.client_status)) {
                      <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'prospect-club-label'">{{'CLIENT_PROFILE.PROSPECT_CLUB' | translate}}</span>
                    }
                </div>
                @if (!showSkeleton()) {
                  <div class="text-sm font-normal cursor-pointer text-right" [ngClass]="isCurrentTenantClient ? 'text-900' : 'text-red-500'" (click)="toggleClientHistory()" [attr.data-testid]="'club-name'">
                      {{ActiveTenant(client())?.name_primary_language || '---'}}
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'club-name-skeleton'"></p-skeleton>
                }
            </div>
            
            <!-- Member Since Row -->
            <div class="flex align-items-center justify-content-between py-1" [attr.data-testid]="'member-since-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-calendar-check text-xs text-500"></i>
                    </div>
                    <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'member-since-label'">{{'CLIENT_PROFILE.MEMBER_SINCE' | translate}}</span>
                </div>
                @if (!showSkeleton()) {
                  <div class="text-sm font-normal text-900 flex align-items-center gap-2 text-right" [attr.data-testid]="'member-since-date'">
                      {{ isDefaultDate(client()?.client_since || '') ? '---' : (client()?.client_since! | datetimeformat: 'mediumDate')}}
                      @if (!isDefaultDate(client()?.client_since || '')) {
                        <span class="text-500 font-normal text-xs" [attr.data-testid]="'member-since-duration'">({{memberSince(client()?.client_since || '').exact}})</span>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'member-since-skeleton'"></p-skeleton>
                }
            </div>

            <!-- Sales Rep Row -->
            <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'sales-rep-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-user text-xs text-500"></i>
                    </div>
                    <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'sales-rep-label'">{{'CLIENT_PROFILE.SALES_REP' | translate}}</span>
                </div>
                @if (!showSkeleton()) {
                  <div class="flex align-items-center gap-2">
                      @if (!changeSalesRep()) {
                        <span class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" (click)="toggleChangeSaleRepDialog()" [attr.data-testid]="'sales-rep-name'">
                            {{ currentSalesRep?.name || '---'}}
                        </span>
                      }
                      @if (!changeSalesRep() && isCurrentTenantClient) {
                        <i class="fa fa-pencil text-xs text-500 cursor-pointer opacity-0 hover-parent-opacity-100 transition-opacity" (click)="toggleChangeSaleRepDialog()"></i>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'sales-rep-skeleton'"></p-skeleton>
                }
            </div>

             <!-- Last Visited center -->
             <div class="flex align-items-center justify-content-between py-1" [attr.data-testid]="'last-visited-section'">
              <div class="flex align-items-center gap-2">
                  <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                      <i class="fa fa-clock text-xs text-500"></i>
                  </div>
                  <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'last-visited-label'">{{'CLIENT_PROFILE.LAST_VISITED_CENTER' | translate}}</span>
              </div>
              @if (!showSkeleton() && (client()?.last_visited_center && client()?.last_visited_center !== '')) {
                <div class="text-sm font-normal text-900 text-right" [attr.data-testid]="'last-visited-date'">{{client()?.last_visited_center }}</div>
              } @else if (!showSkeleton()) {
                <span class="text-sm text-500" [attr.data-testid]="'last-visited-dash'">---</span>
              }
              @if (showSkeleton()) {
                <p-skeleton width="6rem" height="1rem" styleClass="mb-2" [attr.data-testid]="'last-visited-skeleton'"></p-skeleton>
              }
          </div>

            <!-- Last Visited Row -->
            <div class="flex align-items-center justify-content-between py-1" [attr.data-testid]="'last-visited-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-clock text-xs text-500"></i>
                    </div>
                    <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'last-visited-label'">{{'CLIENT_PROFILE.LAST_VISITED_DATE' | translate}}</span>
                </div>
                @if (!showSkeleton() && (client()?.last_visited_center && client()?.last_visited_center !== '')) {
                  <div class="text-sm font-normal text-900 text-right" [attr.data-testid]="'last-visited-date'">{{client()?.last_visited_date_time | DayAgo }}</div>
                } @else if (!showSkeleton()) {
                  <span class="text-sm text-500" [attr.data-testid]="'last-visited-dash'">---</span>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" styleClass="mb-2" [attr.data-testid]="'last-visited-skeleton'"></p-skeleton>
                }
            </div>

            <!-- Channel Row -->
            <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'channel-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-wifi text-xs text-500"></i>
                    </div>
                    <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'channel-label'">{{'CLIENT_PROFILE.CHANNEL' | translate}}</span>
                </div>
                @if (!showSkeleton()) {
                  <div class="text-right">
                      @if (!changeChannel()) {
                        <div (click)="changeChannel.set(true)" class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" [attr.data-testid]="'channel-value'">{{client()?.channel}}</div>
                      }
                      @if (changeChannel()) {
                        <p-select formControlName="channel" dropdownIcon="fa fa-angle-down text-xs" styleClass="w-full text-sm border-round-md" [options]="channelCollection" appendTo="body" optionLabel="channel" (onChange)="onChannelUpdate()" [attr.aria-label]="'channel-dropdown'">
                            <ng-template pTemplate="selectedItem">
                                @if (updateClientFormGroup.get('channel')?.value) {
                                  <div class="text-sm font-normal text-900" [attr.data-testid]="'selected-channel'">{{updateClientFormGroup.get('channel')?.value.channel}}</div>
                                }
                            </ng-template>
                            <ng-template let-channel pTemplate="item">
                                <div class="text-sm" (click)="changeChannel.set(false)" [attr.data-testid]="'channel-option'">{{channel.channel}}</div>
                            </ng-template>
                        </p-select>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'channel-skeleton'"></p-skeleton>
                }
            </div>

            <!-- Source Row -->
            <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'source-section'">
                <div class="flex align-items-center gap-2">
                    <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                        <i class="fa fa-share-nodes text-xs text-500"></i>
                    </div>
                    <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'source-label'">{{'CLIENT_PROFILE.SOURCE' | translate}}</span>
                </div>
                @if (!showSkeleton()) {
                  <div class="text-right">
                      @if (!changeSource()) {
                        <div (click)="changeSource.set(true)" class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" [attr.data-testid]="'source-value'">{{client()?.source}}</div>
                      }
                      @if (changeSource()) {
                        <div class="w-full">
                            <p-select formControlName="source" dropdownIcon="fa fa-angle-down text-xs" styleClass="w-full text-sm border-round-md" [options]="Source()" appendTo="body" (onChange)="onSourceUpdate()" [attr.aria-label]="'source-dropdown'">
                                <ng-template pTemplate="selectedItem">
                                    @if (updateClientFormGroup.get('source')?.value) {
                                      <div class="text-sm font-normal text-900" [attr.data-testid]="'selected-source'">{{updateClientFormGroup.get('source')?.value}}</div>
                                    }
                                </ng-template>
                                <ng-template let-source pTemplate="item">
                                    <div class="text-sm" (click)="source !== 'Others' ? changeSource.set(false) : null" [attr.data-testid]="'source-option'">{{source}}</div>
                                </ng-template>
                            </p-select>
                            
                            @if (updateClientFormGroup.get('source')?.value === 'Others') {
                              <div class="mt-2 flex gap-1" [attr.data-testid]="'custom-source-input-group'">
                                  <input type="text" class="text-sm w-full p-inputtext-sm" pInputText formControlName="customSource" placeholder="Enter Source" [attr.aria-label]="'custom-source-input'" />
                                  <button (click)="onCustomSourceUpdate()" icon="fa fa-check" pButton class="p-button-sm" [attr.aria-label]="'update-custom-source-button'"></button>
                              </div>
                            }
                        </div>
                      }
                  </div>
                }
                @if (showSkeleton()) {
                  <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'source-skeleton'"></p-skeleton>
                }
            </div>
        </div>
        <!-- Client Info Card -->

        <!-- Contact Details Section -->
        <div [attr.data-testid]="'client-details-section'" class="mt-3 border-top-1 surface-border pt-3">
            <button type="button" class="flex align-items-center mb-2 w-full p-0 border-none bg-transparent cursor-pointer text-left" (click)="toggleClientDetailsSection()" [attr.aria-label]="'client-details-toggle'" [attr.aria-expanded]="clientDetailsExpanded()">
                <div class="border-left-2 border-primary h-1rem mr-2"></div>
                <span class="text-xs font-bold text-900 uppercase letter-spacing-1" [attr.aria-label]="'client-details-header'">{{'CLIENT_PROFILE.CLIENT_DETAILS' | translate }}</span>
                <i class="fa ml-2 text-xs text-500 transition-transform" [ngClass]="clientDetailsExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </button>
            @if (clientDetailsExpanded()) {
            <div class="flex flex-column gap-1 text-sm pb-2">
                <!-- Mobile -->
                <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'mobile-section'">
                    <div class="flex align-items-center gap-2">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-phone text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'mobile-label'">{{'CLIENT_PROFILE.MOBILE' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right">
                          @if (!changeMobile()) {
                            <div (click)="isCurrentTenantClient ? changeMobile.set(true) : null" class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" [attr.data-testid]="'mobile-number'">
                                {{client()?.mobile_country_code}}{{client()?.mobile_number}} 
                            </div>
                          }

                          @if (changeMobile()) {
                            <div class="p-inputgroup" [attr.data-testid]="'mobile-input-group'">
                                <ngx-intl-tel-input
                                    [cssClass]="updateClientFormGroup.get('mobile_number')?.invalid ? 'p-invalid text-sm border-round-md':'text-sm border-round-md'"
                                    [enableAutoCountrySelect]="false"
                                    [enablePlaceholder]="true"
                                    [searchCountryFlag]="false"
                                    [selectFirstCountry]="false"
                                    [selectedCountryISO]="clientMobileNoCountry() ? clientMobileNoCountry() : tenantCountry()"
                                    [onlyCountries]="onlyCountries()"
                                    [maxLength]="15"
                                    [phoneValidation]="true"
                                    [separateDialCode]="true"
                                    formControlName="mobile_number"
                                    [attr.aria-label]="'mobile-input'"
                                >
                                </ngx-intl-tel-input>
                                <button (click)="onMobileNumberUpdate();" icon="fa fa-check" pButton class="p-button-sm p-button-text" [attr.aria-label]="'update-mobile-button'"></button>
                            </div>
                          }
                      </div>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="8rem" height="1rem" styleClass="mb-2" [attr.data-testid]="'mobile-skeleton'"></p-skeleton>
                    }
                </div>
                
                <!-- Email -->
                <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'email-section'">
                    <div class="flex align-items-center gap-2">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-envelope text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'email-label'">{{'CLIENT_PROFILE.EMAIL' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right flex-1 pl-3" style="min-width: 0;">
                          @if (!changeEmail()) {
                            <div (click)="isCurrentTenantClient ? changeEmail.set(true) : null" class="text-sm font-normal text-900 cursor-pointer white-space-nowrap overflow-hidden text-overflow-ellipsis hover:text-primary transition-colors" [attr.data-testid]="'email-value'">
                                {{client()?.email_id}}
                            </div>
                          }
                          @if (changeEmail()) {
                            <div class="p-inputgroup w-full" [attr.data-testid]="'email-input-group'">
                                <input type="email" class="text-sm w-full p-inputtext-sm" pInputText formControlName="email_id" placeholder="Enter Email" (keyup.enter)="onEmailUpdate()" (blur)="onEmailUpdate()" [attr.aria-label]="'email-input'" />
                                <button (click)="changeEmail.set(false)" icon="fa fa-check" pButton class="p-button-sm p-button-text" [attr.aria-label]="'update-email-button'"></button>
                            </div>
                          }
                      </div>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="8rem" height="1rem" [attr.data-testid]="'email-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- Birthday -->
                <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'birthday-section'">
                    <div class="flex align-items-center gap-2">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-cake-candles text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'birthday-label'">{{'CLIENT_PROFILE.BIRTHDAY' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right">
                          @if (!changeDOB()) {
                            <div (click)="isCurrentTenantClient ? changeDOB.set(true) : null" class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" [attr.data-testid]="'birthday-value'">{{ isDefaultDate(client()?.date_of_birth || '') ? '---' : (client()?.date_of_birth! | datetimeformat: 'mediumDate')}}</div>
                          }
                          <div class="mt-2">
                              @if (changeDOB()) {
                                <p-datepicker
                                    formControlName="date_of_birth"
                                    [showIcon]="true"
                                    icon="fa fa-calendar font-light"
                                    dateFormat="M dd,yy"
                                    [yearNavigator]="true"
                                    yearRange="1920:2030"
                                    placeholder="Select Date"
                                    inputStyleClass="text-sm w-full"
                                    styleClass="w-full text-sm"
                                    appendTo="body"
                                    (onSelect)="onDOBUpdate()"
                                    [attr.aria-label]="'birthday-calendar'"
                                ></p-datepicker>
                              }
                          </div>
                      </div>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="6rem" height="1rem" [attr.data-testid]="'birthday-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- Organization (Conditional) -->
                @if (client() && client().organisation) {
                  <div class="flex align-items-center justify-content-between py-1" [attr.data-testid]="'organization-section'">
                       <div class="flex align-items-center gap-2">
                          <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                              <i class="fa fa-sitemap text-xs text-500"></i>
                          </div>
                          <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'organization-label'">{{'CLIENT_PROFILE.ORGANIZATION' | translate}}</span>
                      </div>
                      @if (!showSkeleton()) {
                        <div class="text-sm font-normal text-900 text-right" [attr.data-testid]="'organization-value'">{{client().organisation.name_primary_language || '-'}}</div>
                      }
                      @if (showSkeleton()) {
                        <p-skeleton width="8rem" height="1rem" [attr.data-testid]="'organization-skeleton'"></p-skeleton>
                      }
                  </div>
                }

                <!-- Gender -->
                <div class="flex align-items-center justify-content-between py-1 group-hover" [attr.data-testid]="'gender-section'">
                    <div class="flex align-items-center gap-2">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-person text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'gender-label'">{{'CLIENT_PROFILE.GENDER' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right" style="min-width: 8rem;">
                          @if (!changeGender()) {
                            <div (click)="isCurrentTenantClient ? changeGender.set(true) : null" class="text-sm font-normal text-900 cursor-pointer hover:text-primary transition-colors" [attr.data-testid]="'gender-value'">{{client()?.gender}}</div>
                          }
                          @if (changeGender()) {
                            <p-select formControlName="gender" dropdownIcon="fa fa-angle-down text-xs" styleClass="w-full text-sm border-round-md" [options]="genderOptions()" appendTo="body" (onChange)="onGenderUpdate()" [attr.aria-label]="'gender-dropdown'">
                                <ng-template pTemplate="selectedItem">
                                    @if (updateClientFormGroup.get('gender')?.value) {
                                      <div class="text-sm font-normal text-900" [attr.data-testid]="'selected-gender'">{{updateClientFormGroup.get('gender')?.value}}</div>
                                    }
                                </ng-template>
                                <ng-template let-gender pTemplate="item">
                                    <div class="text-sm" (click)="changeGender.set(false)" [attr.data-testid]="'gender-option'">{{gender}}</div>
                                </ng-template>
                            </p-select>
                          }
                      </div>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="6rem" height="1rem" styleClass="mb-2" [attr.data-testid]="'gender-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- Emergency Contact -->
                <div class="flex justify-content-between py-1 group-hover transition-colors border-round hover:surface-50 -mx-2 px-2" 
                    [attr.data-testid]="'emergency-contact-section'"
                    (mouseenter)="showEcEdit.set(true)" (mouseleave)="showEcEdit.set(false)">
                    <div class="flex align-items-center gap-2 align-self-start mt-1">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-triangle-exclamation text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'emergency-contact-label'">{{'CLIENT_PROFILE.EMERGENCY_CONTACT' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right flex-1 pl-3">
                          @if (!changeEmergencyContact()) {
                            <div class="flex flex-column align-items-end gap-1 cursor-pointer"
                                 (click)="isCurrentTenantClient ? changeEmergencyContact.set(true) : null"
                                 [attr.data-testid]="'emergency-contact-value'">
                                <span class="text-sm font-normal text-900 hover:text-primary transition-colors">{{client()?.emergency_contact_person || '---'}}</span>
                                <span class="text-xs text-600">{{client()?.emergency_contact_number}}</span>
                                @if (showEcEdit() && isCurrentTenantClient) {
                                  <i class="fa fa-pencil text-xs text-500 absolute right-0 mr-2" 
                                     title="Edit"></i>
                                }
                            </div>
                          }
                          @if (changeEmergencyContact()) {
                            <div class="flex flex-column gap-2" [attr.data-testid]="'emergency-contact-input-group'">
                                <input type="text" class="text-sm w-full p-inputtext-sm" pInputText formControlName="emergency_contact_person" placeholder="Enter Name" [attr.aria-label]="'emergency-contact-name-input'" />
                                <div class="flex gap-1">
                                    <ngx-intl-tel-input
                                        [cssClass]="updateClientFormGroup.get('emergency_contact_number')?.invalid?'p-invalid text-sm w-full':'text-sm w-full'"
                                        [enableAutoCountrySelect]="false"
                                        [enablePlaceholder]="true"
                                        [searchCountryFlag]="false"
                                        [selectFirstCountry]="false"
                                        [selectedCountryISO]="tenantCountry()"
                                        [onlyCountries]="onlyCountries()"
                                        [maxLength]="15"
                                        [phoneValidation]="true"
                                        [separateDialCode]="true"
                                        [numberFormat]="PhoneNumberFormat.National"
                                        formControlName="emergency_contact_number"
                                        [attr.aria-label]="'emergency-contact-number-input'"
                                    >
                                    </ngx-intl-tel-input>
                                    <button (click)="onEmergencyContactUpdate()" icon="fa fa-check" 
                                    pButton class="p-button-sm" [attr.aria-label]="'update-emergency-contact-button'"></button>
                                </div>
                            </div>
                          }
                      </div>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="8rem" height="1rem" styleClass="mb-2" [attr.data-testid]="'emergency-contact-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- Address -->
                <div class="flex justify-content-between py-1" [attr.data-testid]="'address-section'">
                    <div class="flex align-items-center gap-2 align-self-center">
                        <div class="w-2rem h-2rem surface-100 border-round-md flex align-items-center justify-content-center flex-shrink-0">
                            <i class="fa fa-location-dot text-xs text-500"></i>
                        </div>
                        <span class="text-xs font-medium text-500 uppercase letter-spacing-1" [attr.aria-label]="'address-label'">{{'CLIENT_PROFILE.ADDRESS' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <div class="text-right flex-1 pl-4 flex align-items-center justify-content-end">
                          @if (!changeAddress()) {
                            <div (click)="isCurrentTenantClient ? changeAddress.set(true) : null" 
                                 class="text-sm font-normal text-900 cursor-pointer line-height-3 text-right break-word hover:text-primary transition-colors" 
                                 [attr.data-testid]="'address-value'">
                                 {{client()?.address || '---'}}
                            </div>
                          }
                          @if (changeAddress()) {
                            <div class="flex flex-column gap-2 w-full" [attr.data-testid]="'address-input-group'">
                                <textarea 
                                    class="text-sm w-full border-round-md" 
                                    pInputTextarea 
                                    formControlName="address" 
                                    placeholder="Enter Address" 
                                    rows="3"
                                    (keyup.enter)="onAddressUpdate()" 
                                    (blur)="onAddressUpdate()" 
                                    [attr.aria-label]="'address-input'">
                                </textarea>
                                <div class="flex justify-content-end">
                                    <button (click)="changeAddress.set(false)" icon="fa fa-check" pButton class="p-button-sm p-button-text" [attr.aria-label]="'update-address-button'"></button>
                                </div>
                            </div>
                          }
                      </div>
                    }
                    @if (showSkeleton()) {
                      <div class="mt-1">
                          <p-skeleton width="100%" height="3rem" [attr.data-testid]="'address-skeleton'"></p-skeleton>
                      </div>
                    }
                </div>
            </div>
            }
        </div>

        <!-- Score Section (shown when score exists, between Client Details and Marketing Preference) -->
        @if (client()?.score) {
          @let scoreStyle = getScoreCategoryStyle(client()?.score?.score_category);
          @let scoreVal = getDisplayScore(client()?.score);
          @let scoreTitleKey = getScoreSectionTitleKey(client()?.score?.score_type);
          @let parsedFactors = parseContributingFactors(client()?.score?.contributing_factors);
          @let parsedActions = parseRecommendedActions(client()?.score?.recommended_actions);
          <div [attr.data-testid]="'score-section'" class="mt-3 border-top-1 surface-border pt-3">
            <button type="button" class="flex align-items-center mb-2 w-full p-0 border-none bg-transparent cursor-pointer text-left" (click)="toggleScoreSection()" [attr.aria-label]="'score-section-toggle'" [attr.aria-expanded]="scoreSectionExpanded()">
              <div class="border-left-2 border-primary h-1rem mr-2"></div>
              <span class="text-xs font-bold text-900 uppercase letter-spacing-1" [attr.aria-label]="'score-section-header'">{{scoreTitleKey | translate}}</span>
              <i class="fa ml-2 text-xs text-500 transition-transform" [ngClass]="scoreSectionExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </button>
            @if (scoreSectionExpanded()) {
              <div class="p-3 border-round-lg flex flex-column gap-3" [attr.data-testid]="'score-details-content'" [style.background]="scoreStyle.cardBg">
                <!-- Gauge and score -->
                <div class="flex flex-column align-items-center" [attr.data-testid]="'score-gauge'">
                  <div class="relative" style="width: 120px; height: 70px;">
                    <svg viewBox="0 0 100 55" class="w-full" style="overflow: visible;">
                      <path d="M 5 50 A 45 45 0 0 1 95 50" fill="none" stroke="var(--p-surface-200)" stroke-width="8" stroke-linecap="round" [attr.data-testid]="'gauge-bg'" />
                      <path d="M 5 50 A 45 45 0 0 1 95 50" fill="none" [attr.stroke]="scoreStyle.gaugeColor" stroke-width="8" stroke-linecap="round" stroke-dasharray="141.4" [attr.stroke-dashoffset]="getGaugeOffset(scoreVal)" [attr.data-testid]="'gauge-fill'" />
                    </svg>
                    <span class="absolute font-bold text-900 text-xl" style="left: 50%; top: 50%; transform: translate(-50%, -70%);" [attr.data-testid]="'churn-score-value'">{{scoreVal}}</span>
                  </div>
                  <span class="font-bold text-sm mt-1" [ngClass]="scoreStyle.textColor" [attr.data-testid]="'score-category-label'">{{scoreStyle.labelKey | translate}}</span>
                </div>
                @if (parsedFactors.length) {
                  <div [attr.data-testid]="'contributing-factors'">
                    <span class="text-xs font-bold text-900 uppercase letter-spacing-1 block mb-1" [attr.aria-label]="'contributing-factors-label'">{{'CLIENT_PROFILE.SCORE.CONTRIBUTING_FACTORS' | translate }}</span>
                    <ul class="m-0 pl-3 text-sm text-700">
                      @for (factor of parsedFactors; track factor) {
                        <li class="mb-1" [attr.data-testid]="'contributing-factor-item'">{{factor}}</li>
                      }
                    </ul>
                  </div>
                }
                @if (client()?.score?.rationale_note) {
                  <div [attr.data-testid]="'rationale-note'">
                    <span class="text-xs font-bold text-900 uppercase letter-spacing-1 block mb-1" [attr.aria-label]="'rationale-note-label'">{{'CLIENT_PROFILE.SCORE.RATIONALE' | translate }}</span>
                    <p class="text-sm text-700 m-0 line-height-3" [attr.data-testid]="'rationale-note-value'">{{client()?.score?.rationale_note}}</p>
                  </div>
                }
                @if (parsedActions.length) {
                  <div [attr.data-testid]="'recommended-actions'">
                    <span class="text-xs font-bold text-900 uppercase letter-spacing-1 block mb-1" [attr.aria-label]="'recommended-actions-label'">{{'CLIENT_PROFILE.SCORE.RECOMMENDED_ACTIONS' | translate }}</span>
                    <ul class="m-0 pl-3 text-sm text-700">
                      @for (action of parsedActions; track action) {
                        <li class="mb-1" [attr.data-testid]="'recommended-action-item'">{{action}}</li>
                      }
                    </ul>
                  </div>
                }
              </div>
            }
          </div>
        }

        <!-- Marketing Preferences - v7 style -->
        <div class="mt-3 border-top-1 surface-border pt-3" formGroupName="marketing_preferences" [attr.data-testid]="'marketing-preferences-section'">
            <button type="button" class="flex align-items-center mb-2 w-full p-0 border-none bg-transparent cursor-pointer text-left" (click)="toggleMarketingPrefSection()" [attr.aria-label]="'marketing-preferences-toggle'" [attr.aria-expanded]="marketingPrefExpanded()">
                <div class="border-left-2 border-primary h-1rem mr-2"></div>
                <strong class="block font-bold text-xs text-900 uppercase letter-spacing-1" [attr.aria-label]="'marketing-preferences-header'">{{'CLIENT_INTERACTION.MARKETING' | translate}}</strong>
                <i class="fa ml-2 text-xs text-500 transition-transform" [ngClass]="marketingPrefExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </button>
            @if (marketingPrefExpanded()) {
            <div class="flex flex-column gap-2">
                <!-- Email Preference -->
                <div class="flex align-items-center justify-content-between p-2 border-round-lg transition-colors" 
                     [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('email')?.value ? 'bg-teal-50' : 'surface-50'"
                     [attr.data-testid]="'email-preference-section'">
                    <div class="flex align-items-center gap-3">
                        <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center transition-colors" 
                             [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('email')?.value ? 'bg-teal-100' : 'surface-200'">
                            <i class="fa fa-envelope text-sm transition-colors" 
                               [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('email')?.value ? 'text-teal-600' : 'text-500'"
                               [attr.data-testid]="'email-preference-icon'"></i>
                        </div>
                        <span class="text-sm font-medium text-700" [attr.aria-label]="'email-preference-label'">{{'CLIENT_PROFILE.EMAIL' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <p-toggleSwitch 
                          formControlName="email"
                          (onChange)="onMarketingPreferenceChange()"
                          [attr.aria-label]="'email-preference-toggle'">
                      </p-toggleSwitch>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="3rem" height="1.5rem" [attr.data-testid]="'email-preference-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- SMS Preference -->
                <div class="flex align-items-center justify-content-between p-2 border-round-lg transition-colors" 
                     [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('sms')?.value ? 'bg-teal-50' : 'surface-50'"
                     [attr.data-testid]="'sms-preference-section'">
                    <div class="flex align-items-center gap-3">
                        <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center transition-colors" 
                             [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('sms')?.value ? 'bg-teal-100' : 'surface-200'">
                            <i class="fa fa-comment text-sm transition-colors" 
                               [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('sms')?.value ? 'text-teal-600' : 'text-500'"
                               [attr.data-testid]="'sms-preference-icon'"></i>
                        </div>
                        <span class="text-sm font-medium text-700" [attr.aria-label]="'sms-preference-label'">{{'CLIENT_PROFILE.SMS' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <p-toggleSwitch 
                          formControlName="sms"
                          (onChange)="onMarketingPreferenceChange()"
                          [attr.aria-label]="'sms-preference-toggle'">
                      </p-toggleSwitch>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="3rem" height="1.5rem" [attr.data-testid]="'sms-preference-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- WhatsApp Preference -->
                <div class="flex align-items-center justify-content-between p-2 border-round-lg transition-colors" 
                     [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('whatsapp')?.value ? 'bg-teal-50' : 'surface-50'"
                     [attr.data-testid]="'whatsapp-preference-section'">
                    <div class="flex align-items-center gap-3">
                        <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center transition-colors" 
                             [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('whatsapp')?.value ? 'bg-teal-100' : 'surface-200'">
                            <i class="fab fa-whatsapp text-sm transition-colors" 
                               [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('whatsapp')?.value ? 'text-teal-600' : 'text-500'"
                               [attr.data-testid]="'whatsapp-preference-icon'"></i>
                        </div>
                        <span class="text-sm font-medium text-700" [attr.aria-label]="'whatsapp-preference-label'">{{'CLIENT_PROFILE.WHATS_APP' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <p-toggleSwitch 
                          formControlName="whatsapp"
                          (onChange)="onMarketingPreferenceChange()"
                          [attr.aria-label]="'whatsapp-preference-toggle'">
                      </p-toggleSwitch>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="3rem" height="1.5rem" [attr.data-testid]="'whatsapp-preference-skeleton'"></p-skeleton>
                    }
                </div>

                <!-- Mobile Notification Preference -->
                <div class="flex align-items-center justify-content-between p-2 border-round-lg transition-colors" 
                     [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('push')?.value ? 'bg-teal-50' : 'surface-50'"
                     [attr.data-testid]="'push-preference-section'">
                    <div class="flex align-items-center gap-3">
                        <div class="w-2rem h-2rem border-round-md flex align-items-center justify-content-center transition-colors" 
                             [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('push')?.value ? 'bg-teal-100' : 'surface-200'">
                            <i class="fa fa-bell text-sm transition-colors" 
                               [ngClass]="updateClientFormGroup.get('marketing_preferences')?.get('push')?.value ? 'text-teal-600' : 'text-500'"
                               [attr.data-testid]="'push-preference-icon'"></i>
                        </div>
                        <span class="text-sm font-medium text-700" [attr.aria-label]="'push-preference-label'">{{'CLIENT_PROFILE.MOBILE_NOTIFICATION' | translate}}</span>
                    </div>
                    @if (!showSkeleton()) {
                      <p-toggleSwitch 
                          formControlName="push"
                          (onChange)="onMarketingPreferenceChange()"
                          [attr.aria-label]="'push-preference-toggle'">
                      </p-toggleSwitch>
                    }
                    @if (showSkeleton()) {
                      <p-skeleton width="3rem" height="1.5rem" [attr.data-testid]="'push-preference-skeleton'"></p-skeleton>
                    }
                </div>
            </div>
            }
        </div>

        <!-- Additional Info Footer -->
        <div class="mt-4 text-xs text-500" [attr.data-testid]="'additional-info-section'">
            @if (client() && client().organisation) {
              <div [attr.data-testid]="'organization-info'">{{'CLIENT_PROFILE.ORGANIZATION' | translate}}: {{client().organisation.name_primary_language}}</div>
            }
            @if (client() && client().national_id) {
              <div [attr.data-testid]="'national-id-info'">{{'CLIENT_PROFILE.NATIONAL_ID' | translate}}: {{client().national_id}}</div>
            }
        </div>
    </form>
</div>

<!-- Dialogs remain unchanged -->
 @if (showCreditBalance()) {
  <fitness-force-client-credit-balance [visible]="showCreditBalance()" (closePopup)="closeCreditPopup($event)" [clientId]="client().id" [attr.data-testid]="'credit-balance-dialog'" />
}

@if (toggleChangeSaleRepDialog$ | async) {
  <change-sales-representative [changeRepresentativeModal]="toggleChangeSaleRepDialog$ | async"
    [clientData$]="clientDetailsObj"
    (closeChangeRepresentativeDialog)="closeChangeSaleRepDialog($event)"
    (updatedRepresentative$)="onUpdatedRepresentative($event)"
    [attr.data-testid]="'change-sales-rep-dialog'"
   />
}

@if (showClientHistory()) {
  <client-history [clientHistoryModal]="showClientHistory()" [clientData$]="clientDetailsObj" (closeClientHistoryDialog)="closeClientHistoryDialog($event)" [attr.data-testid]="'client-history-dialog'" />
}

